import { useEffect, useRef, useState } from 'react';
import { MentionChipHoverPreview, type MentionChipHoverAnchor } from '../MentionChipHoverPreview';
import { speakerInitial } from './speakerLineGroups';
// 映像・ビジュアル欄の @メンション読み取り表示チップ (MentionTokenInlineText) と
// 同じ寸法・トーンに揃えるため、専用 CSS を新設せずこのモジュールをそのまま流用する
// (size='sm'・読み取り表示 DialogueLinesView 用)。
import mentionChipStyles from '../MentionTokenChipOverlay.module.css';
// size='lg' (セリフエディタのトリガー用・要件1) はこちらの専用スタイルを使う。
import dialogueLineStyles from './DialogueLines.module.css';

// MentionTokenInlineText / MentionTokenChipOverlay と同じ値で揃える。
const HOVER_SHOW_DELAY_MS = 150;

interface DialogueSpeakerChipProps {
  /** 映像・ビジュアル欄の @メンションバッジと同じ文字 (A, B, C…)。 */
  badge: string;
  thumbnailUrl: string | null;
  name: string;
  /**
   * 'sm' (既定): @メンションチップと同じ極小チップ (枠線+塗りあり)。現在は未使用だが
   * 汎用のため残置。
   * 'lg': 枠線・塗りを一切持たない拡大顔写真+右下隅のバッジオーバーレイ。セリフエディタの
   * 話者トリガー (DialogueLinesEditor) と読み取り表示 (DialogueLinesView) の両方で使う
   * (2026-08-17 ユーザー指示で読み取り側も統一)。
   */
  size?: 'sm' | 'lg';
  /**
   * 写真が無いときのフォールバック図柄 (size='lg' のみ)。person (既定) = ダミー人物 /
   * mic = ナレーション (テロップ話者。docs/features/scenario-telop-speaker-plan.md §3.7)。
   */
  glyph?: 'person' | 'mic';
}

/**
 * セリフ行の話者チップ (顔写真+バッジ文字)。映像・ビジュアル欄の @メンション
 * トークンの読み取り表示チップ (MentionTokenInlineText.tsx) と同じ見た目・
 * ホバー拡大プレビュー (MentionChipHoverPreview) を流用する
 * (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)。
 */
export function DialogueSpeakerChip({ badge, thumbnailUrl, name, size = 'sm', glyph = 'person' }: DialogueSpeakerChipProps) {
  const [hoverAnchor, setHoverAnchor] = useState<MentionChipHoverAnchor | null>(null);
  const showTimeoutRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (showTimeoutRef.current !== null) window.clearTimeout(showTimeoutRef.current);
  }, []);

  const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (showTimeoutRef.current !== null) window.clearTimeout(showTimeoutRef.current);
    const rect = event.currentTarget.getBoundingClientRect();
    const anchor: MentionChipHoverAnchor = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    showTimeoutRef.current = window.setTimeout(() => {
      showTimeoutRef.current = null;
      setHoverAnchor(anchor);
    }, HOVER_SHOW_DELAY_MS);
  };

  const handleMouseLeave = () => {
    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setHoverAnchor(null);
  };

  if (size === 'lg') {
    // 写真が無く名前がある話者 (自由ラベル・登場人物宣言・顔写真未登録の俳優) は、
    // ダミー人物シルエットの上に名前の頭文字を重ねて誰か分かるようにする
    // (2026-09-18 ユーザー指示)。ナレーション (mic) は人物ではないので付けない。
    const initial = !thumbnailUrl && glyph === 'person' ? speakerInitial(name) : '';
    return (
      <span
        className={dialogueLineStyles.speakerChipLg}
        data-testid="dialogue-speaker-chip"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={dialogueLineStyles.speakerChipLgPhotoWrap}>
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              draggable={false}
              className={dialogueLineStyles.speakerChipLgPhoto}
            />
          ) : (
            // 自由入力等で写真が無い話者は、同じ円サイズのダミー人物シルエット
            // (2026-08-17 ユーザー指示)。
            <span
              className={dialogueLineStyles.speakerChipLgPhotoFallback}
              aria-hidden="true"
              data-glyph={glyph}
              data-initial={initial || undefined}
            >
              {glyph === 'mic' ? (
                // ナレーション (画面外の語り手): マイク
                <svg viewBox="0 0 28 28" width="24" height="24">
                  <rect x="10.5" y="5" width="7" height="12" rx="3.5" fill="currentColor" />
                  <path d="M8 14.5a6 6 0 0 0 12 0" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                  <path d="M14 20.5v3M11 23.5h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 28 28" width="24" height="24">
                  <circle cx="14" cy="10.5" r="4.5" fill="currentColor" />
                  <path d="M5.5 23.5c1.7-4.8 5-7 8.5-7s6.8 2.2 8.5 7" fill="currentColor" />
                </svg>
              )}
              {initial && <span className={dialogueLineStyles.speakerChipLgInitial}>{initial}</span>}
            </span>
          )}
          {badge && <span className={dialogueLineStyles.speakerChipLgBadge}>{badge}</span>}
        </span>
        {/* ホバー拡大プレビュー。自由入力 (写真なし) でも表示する — ダミーアイコンでは
            話者名が見えないため、ホバーで label を確認できるようにする
            (2026-08-17 ユーザー指示で一旦無効→再度有効化)。写真なしの場合は
            MentionChipHoverPreview が名前のみ表示する。 */}
        {hoverAnchor && name && (
          <MentionChipHoverPreview anchor={hoverAnchor} thumbnailUrl={thumbnailUrl} name={name} kind="actor" />
        )}
      </span>
    );
  }

  return (
    <span
      className={mentionChipStyles.inlineChip}
      data-testid="dialogue-speaker-chip"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          draggable={false}
          className={`${mentionChipStyles.thumb} ${mentionChipStyles.thumbActor}`}
        />
      )}
      <span className={mentionChipStyles.label}>{badge}</span>
      {hoverAnchor && (
        <MentionChipHoverPreview anchor={hoverAnchor} thumbnailUrl={thumbnailUrl} name={name} kind="actor" />
      )}
    </span>
  );
}
